import { IDBPDatabase, openDB } from "idb";
import { Attachment } from "../models/common";

interface DBStructure {
    comments: {
        id: string;
        text: string;
        files: File[];
        createdAt: number;
    };
    description: {
        id: string;
        text: string;
        attachments: Attachment[];
        files: File[];
        createdAt: number;
        isEditing: boolean;
    };
}

let dbPromise: Promise<IDBPDatabase<DBStructure>>;

export const initDB = () => {
    dbPromise = openDB<DBStructure>("ProperIndexedDB", 1, {
        upgrade(db) {
            if (!db.objectStoreNames.contains("comments")) {
                db.createObjectStore("comments", { keyPath: "id" });
            }
            if (!db.objectStoreNames.contains("description")) {
                db.createObjectStore("description", { keyPath: "id" });
            }
        },
    });
};

export const saveCommentToIndexedDB = async (
    key: string,
    text: string,
    files: File[],
): Promise<void> => {
    const db = await dbPromise;

    // Convert files to a savable format
    const fileData = await Promise.all(
        files.map(async (file) => ({
            name: file.name,
            type: file.type,
            size: file.size,
            data: await file.arrayBuffer(), // Convert File to ArrayBuffer
        })),
    );

    await db.put("comments", { id: key, text, files: fileData, createdAt: Date.now() });
};

export const getCommentFromIndexedDB = async (key: string): Promise<DBStructure["comments"]> => {
    const db = await dbPromise;
    const comment = await db.get("comments", key);

    if (comment && comment.files) {
        // Convert stored data back to File objects
        comment.files = comment.files.map(
            (fileData: { name: string; type: string; size: number; data: ArrayBuffer }) =>
                new File([fileData.data], fileData.name, { type: fileData.type }),
        );
    }

    return comment;
};

export const deleteCommentFromIndexedDB = async (key: string): Promise<void> => {
    const db = await dbPromise;
    await db.delete("comments", key);
};

export const saveDescriptionToIndexedDB = async (
    key: string,
    text: string,
    attachments: Attachment[],
    files: File[],
    isEditing: boolean,
): Promise<void> => {
    const db = await dbPromise;

    // Convert files to a savable format
    const fileData = await Promise.all(
        files.map(async (file) => ({
            name: file.name,
            type: file.type,
            size: file.size,
            data: await file.arrayBuffer(), // Convert File to ArrayBuffer
        })),
    );

    const attachmentsData = attachments.map((attachment) => ({
        id: attachment.id,
        fileId: attachment.fileId,
        url: attachment.url,
        name: attachment.name,
    }));

    await db.put("description", {
        id: key,
        text,
        attachments: attachmentsData,
        files: fileData,
        createdAt: Date.now(),
        isEditing,
    });
};

export const getDescriptionFromIndexedDB = async (
    key: string,
): Promise<DBStructure["description"]> => {
    const db = await dbPromise;
    const description = await db.get("description", key);

    if (description && description.files) {
        // Convert stored data back to File objects
        description.files = description.files.map(
            (fileData: { name: string; type: string; size: number; data: ArrayBuffer }) =>
                new File([fileData.data], fileData.name, { type: fileData.type }),
        );
    }

    return description;
};

export const deleteDescriptionFromIndexedDB = async (key: string): Promise<void> => {
    const db = await dbPromise;
    await db.delete("description", key);
};

/**
 * Deletes all entries older than a week,
 * This helps prevent unnecessary database growth
 * @private
 */
const deleteOldEntries = async (): Promise<void> => {
    const db = await dbPromise;
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

    const deleteOld = async (storeName: keyof DBStructure) => {
        const tx = db.transaction(storeName, "readwrite");
        const store = tx.objectStore(storeName);
        const allKeys = await store.getAllKeys();
        for (const key of allKeys) {
            const entry = await store.get(key);
            if (entry && entry.createdAt < oneWeekAgo) {
                await store.delete(key);
            }
        }
        await tx.done;
    };

    await deleteOld("comments");
    await deleteOld("description");
};

setInterval(deleteOldEntries, 24 * 60 * 60 * 1000); // Runs once a day
